/**
 * CameraPopup - Floating, draggable, resizable camera viewer.
 * • Windy cameras: uses the Windy embed player (windyId required)
 * • HLS streams (.m3u8): plays live video via HLS.js
 * • Seed / DOT cameras: shows live thumbnail image via server proxy, refreshed every 60 s
 * • "Open source" button always available for direct viewing
 */
import { useEffect, useRef, useState } from 'react'
import { apiPath } from './runtime'
import type { CameraItem } from './types'

// Extend window for HLS.js loaded from CDN
declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Hls: any
  }
}

/** Load HLS.js from CDN once, returns a promise that resolves when ready */
let _hlsScriptPromise: Promise<void> | null = null
function loadHlsJs(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve()
  if (window.Hls) return Promise.resolve()
  if (_hlsScriptPromise) return _hlsScriptPromise
  _hlsScriptPromise = new Promise((resolve) => {
    const s = document.createElement('script')
    s.src = 'https://cdnjs.cloudflare.com/ajax/libs/hls.js/1.5.13/hls.min.js'
    s.async = true
    s.onload  = () => resolve()
    s.onerror = () => resolve()   // still resolve so we fall back gracefully
    document.head.appendChild(s)
  })
  return _hlsScriptPromise
}

type Props = {
  camera: CameraItem
  onClose: () => void
}

export default function CameraPopup({ camera, onClose }: Props) {
  const initW = 560, initH = 380
  const [pos, setPos]   = useState(() => ({
    x: Math.max(0, window.innerWidth  / 2 - initW / 2),
    y: Math.max(0, window.innerHeight / 2 - initH / 2),
  }))
  const [size, setSize] = useState({ w: initW, h: initH })
  const [dragging,  setDragging]  = useState(false)
  const [resizing,  setResizing]  = useState(false)
  const drag   = useRef({ mx: 0, my: 0, px: 0, py: 0 })
  const resize = useRef({ mx: 0, my: 0, w: initW, h: initH })

  useEffect(() => {
    if (!dragging && !resizing) return
    const onMove = (e: MouseEvent) => {
      if (dragging) {
        setPos({
          x: Math.max(0, e.clientX - drag.current.mx + drag.current.px),
          y: Math.max(0, e.clientY - drag.current.my + drag.current.py),
        })
      }
      if (resizing) {
        setSize({
          w: Math.max(360, resize.current.w + e.clientX - resize.current.mx),
          h: Math.max(260, resize.current.h + e.clientY - resize.current.my),
        })
      }
    }
    const onUp = () => { setDragging(false); setResizing(false) }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
    return () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
    }
  }, [dragging, resizing])

  // Only use Windy embed for cameras that actually have a Windy player ID
  const windyEmbedUrl = camera.windyId
    ? `https://webcams.windy.com/webcams/public/embed/player/${camera.windyId}`
    : null
  const publicCameraUrl = camera.publicUrl || camera.embedUrl ||
    (camera.windyId ? `https://webcams.windy.com/webcams/public/${camera.windyId}` : '')

  const [thumbTs, setThumbTs] = useState(() => Date.now())
  const [thumbErr, setThumbErr] = useState(false)

  // ── HLS video player ────────────────────────────────────────────────────
  const videoRef  = useRef<HTMLVideoElement>(null)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const hlsRef    = useRef<any>(null)
  const [videoErr, setVideoErr]   = useState(false)
  const [videoReady, setVideoReady] = useState(false)

  const isHls = !!(camera.streamUrl && camera.streamUrl.includes('.m3u8'))
  const isMp4 = !!(camera.streamUrl && camera.streamUrl.match(/\.(mp4|webm|ogv)(\?|$)/i))
  const hasStream = isHls || isMp4

  useEffect(() => {
    if (!hasStream || !videoRef.current) return
    setVideoErr(false); setVideoReady(false)

    const attach = () => {
      const video = videoRef.current
      if (!video) return

      // Destroy any previous instance
      if (hlsRef.current) { try { hlsRef.current.destroy() } catch (_) {} hlsRef.current = null }

      if (isHls && window.Hls && window.Hls.isSupported()) {
        const hls = new window.Hls({
          enableWorker: false,
          // Aggressive prefetch for fast start
          startFragPrefetch: true,
          // Small buffer — start playing quickly
          maxBufferLength: 8,
          maxBufferSize: 8 * 1000 * 1000,
          maxMaxBufferLength: 20,
          // Stay close to live edge
          liveSyncDurationCount: 2,
          liveMaxLatencyDurationCount: 4,
          liveDurationInfinity: true,
          // Faster retries
          fragLoadingMaxRetry: 4,
          manifestLoadingMaxRetry: 3,
          levelLoadingMaxRetry: 3,
        })
        hlsRef.current = hls
        hls.on(window.Hls.Events.ERROR, (_evt: unknown, data: { fatal?: boolean }) => {
          if (data.fatal) setVideoErr(true)
        })
        hls.loadSource(camera.streamUrl)
        hls.attachMedia(video)
        hls.on(window.Hls.Events.MANIFEST_PARSED, () => {
          setVideoReady(true)
          video.play().catch(() => {})
        })
      } else if (isHls && video.canPlayType('application/vnd.apple.mpegurl')) {
        // Safari has native HLS
        video.src = camera.streamUrl
        video.addEventListener('loadedmetadata', () => { setVideoReady(true); video.play().catch(() => {}) }, { once: true })
      } else if (isMp4) {
        video.src = camera.streamUrl
        video.addEventListener('loadedmetadata', () => { setVideoReady(true); video.play().catch(() => {}) }, { once: true })
      } else {
        setVideoErr(true)
      }
    }

    if (isHls) {
      loadHlsJs().then(attach)
    } else {
      attach()
    }

    return () => {
      if (hlsRef.current) { try { hlsRef.current.destroy() } catch (_) {} hlsRef.current = null }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [camera.streamUrl])

  // Resolve thumbnail source:
  // • data: base64 (Shodan)  → use directly, append _t to state not the src (b64 never changes)
  // • http(s): (DOT/EarthCam) → route via CORS proxy with cache-buster timestamp
  // • relative / empty       → use as-is
  const rawThumb = camera.thumbnail || camera.screenshot || ''
  const proxyThumb = rawThumb.startsWith('data:')
    ? rawThumb                                                                  // Shodan b64 — direct
    : rawThumb.startsWith('http')
      ? `${apiPath('/proxy-image')}?url=${encodeURIComponent(rawThumb)}&_t=${thumbTs}`   // DOT — CORS proxy
      : rawThumb

  // Shodan screenshots are point-in-time captures; DOT/EarthCam URLs refresh every 1–5 min.
  const isShodanCamera  = camera.source === 'shodan'
  const isRefreshable   = !windyEmbedUrl && !isShodanCamera && rawThumb.startsWith('http')

  // Auto-refresh DOT/EarthCam thumbnails every 60 s
  useEffect(() => {
    if (!isRefreshable) return
    setThumbTs(Date.now()); setThumbErr(false)
    const t = window.setInterval(() => setThumbTs(Date.now()), 60_000)
    return () => window.clearInterval(t)
  }, [camera.id]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      style={{
        position: 'fixed', left: pos.x, top: pos.y,
        width: size.w, height: size.h, zIndex: 9999,
        background: '#050505', border: '1px solid rgba(255,102,0,0.35)',
        borderRadius: 4, display: 'flex', flexDirection: 'column',
        boxShadow: '0 0 0 1px rgba(255,102,0,0.08) inset, 0 24px 80px rgba(0,0,0,0.95)',
        overflow: 'hidden',
      }}
    >
      {/* Corner accents */}
      <div style={{ position:'absolute', top:0, left:0, width:12, height:12, borderTop:'1px solid var(--orange)', borderLeft:'1px solid var(--orange)', pointerEvents:'none', zIndex:5 }} />
      <div style={{ position:'absolute', bottom:0, right:0, width:12, height:12, borderBottom:'1px solid var(--orange)', borderRight:'1px solid var(--orange)', pointerEvents:'none', zIndex:5 }} />

      {/* Drag handle header */}
      <div
        style={{
          background: 'rgba(255,102,0,0.07)', padding: '8px 12px',
          display: 'flex', alignItems: 'center', gap: 10,
          borderBottom: '1px solid rgba(255,102,0,0.2)',
          cursor: dragging ? 'grabbing' : 'grab', userSelect: 'none', flexShrink: 0,
        }}
        onMouseDown={e => {
          setDragging(true)
          drag.current = { mx: e.clientX, my: e.clientY, px: pos.x, py: pos.y }
        }}
      >
        <span style={{ fontSize: 14, color: 'var(--orange)' }}>📷</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 12, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'Courier New, monospace' }}>
            {camera.name}
          </div>
          <div style={{ fontSize: 9, color: 'var(--muted)', fontFamily: 'monospace', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            {camera.city}{camera.state ? `, ${camera.state}` : ''} · via {camera.source.toUpperCase()}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          {(publicCameraUrl || windyEmbedUrl) && (
            <a
              href={publicCameraUrl || windyEmbedUrl || '#'}
              target="_blank" rel="noreferrer"
              style={{ color: 'var(--orange)', fontSize: 11, textDecoration: 'none', padding: '2px 6px', border: '1px solid rgba(255,102,0,0.3)', borderRadius: 3, fontFamily: 'monospace' }}
              onMouseDown={e => e.stopPropagation()}
            >{camera.windyId ? '↗ Windy' : '↗ Open'}</a>
          )}
          <button
            style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 16, padding: 0, lineHeight: 1 }}
            onClick={onClose}
            onMouseDown={e => e.stopPropagation()}
          >&#x2715;</button>
        </div>
      </div>

      {/* Stream / player area */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', background: '#000', position: 'relative' }}>
        {windyEmbedUrl ? (
          /* ── Windy cameras: use embedded player ── */
          <iframe
            src={windyEmbedUrl}
            style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
            allow="autoplay; fullscreen"
            referrerPolicy="no-referrer"
            title={camera.name}
          />
        ) : hasStream && !videoErr ? (
          /* ── HLS / MP4 live stream ── */
          <div style={{ width: '100%', height: '100%', position: 'relative', background: '#000' }}>
            {/* Poster thumbnail shown while stream loads */}
            {!videoReady && proxyThumb && (
              <img
                src={proxyThumb}
                alt={camera.name}
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: 0.5 }}
              />
            )}
            <video
              ref={videoRef}
              muted
              playsInline
              controls
              style={{ width: '100%', height: '100%', display: 'block', objectFit: 'contain' }}
              onError={() => setVideoErr(true)}
              poster={proxyThumb || undefined}
            />
            {/* Loading spinner overlay */}
            {!videoReady && !videoErr && (
              <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                <div style={{ width: 32, height: 32, border: '3px solid rgba(255,102,0,0.15)',
                  borderTopColor: 'var(--orange)', borderRadius: '50%',
                  animation: 'spin 0.9s linear infinite' }} />
                <div style={{ marginTop: 10, fontSize: 9, fontFamily: 'monospace', color: 'var(--orange)',
                  letterSpacing: '0.15em' }}>CONNECTING…</div>
              </div>
            )}
            {/* LIVE badge */}
            {videoReady && (
              <div style={{ position: 'absolute', top: 8, left: 8, display: 'flex', alignItems: 'center',
                gap: 5, background: 'rgba(0,0,0,0.65)', borderRadius: 3, padding: '2px 7px' }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#0f0',
                  display: 'inline-block', boxShadow: '0 0 5px #0f0' }} />
                <span style={{ fontSize: 9, fontFamily: 'monospace', color: '#0f0', letterSpacing: '0.1em' }}>LIVE</span>
              </div>
            )}
            {/* Open source link */}
            {publicCameraUrl && (
              <a href={publicCameraUrl} target="_blank" rel="noreferrer"
                style={{ position: 'absolute', bottom: 6, right: 8, fontSize: 9, fontFamily: 'monospace',
                  color: 'rgba(255,102,0,0.6)', textDecoration: 'none' }}>
                Open source &#x2197;
              </a>
            )}
          </div>
        ) : hasStream && videoErr ? (
          /* ── Stream failed, fall back to thumbnail ── */
          proxyThumb && !thumbErr ? (
            <div style={{ width: '100%', height: '100%', position: 'relative' }}>
              <img key={thumbTs} src={proxyThumb} alt={camera.name}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                onError={() => setThumbErr(true)} />
              <div style={{ position: 'absolute', top: 8, left: 8, background: 'rgba(0,0,0,0.7)',
                borderRadius: 3, padding: '2px 8px', fontSize: 9, fontFamily: 'monospace',
                color: '#f90', letterSpacing: '0.1em' }}>
                ⚠ STREAM UNAVAILABLE · LAST SNAPSHOT
              </div>
              <button onClick={() => { setVideoErr(false) }}
                style={{ position: 'absolute', top: 8, right: 8, background: 'rgba(0,0,0,0.55)',
                  border: '1px solid rgba(255,102,0,0.4)', borderRadius: 3, color: 'var(--orange)',
                  fontSize: 11, cursor: 'pointer', padding: '2px 8px', fontFamily: 'monospace' }}>
                ↺ Retry stream
              </button>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: 24 }}>
              <div style={{ fontSize: 36, marginBottom: 10, opacity: 0.4 }}>📡</div>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--grey)', marginBottom: 6 }}>Stream unavailable</div>
              <button onClick={() => setVideoErr(false)}
                style={{ fontSize: 10, color: 'var(--orange)', background: 'none',
                  border: '1px solid rgba(255,102,0,0.3)', borderRadius: 3,
                  padding: '3px 10px', cursor: 'pointer', fontFamily: 'monospace' }}>
                ↺ Retry
              </button>
            </div>
          )
        ) : proxyThumb && !thumbErr ? (
          /* ── DOT / Seed cameras: show proxied live thumbnail ── */
          <div style={{ width: '100%', height: '100%', position: 'relative' }}>
            <img
              key={thumbTs}
              src={proxyThumb}
              alt={camera.name}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              onError={() => setThumbErr(true)}
            />
            {/* Live / OSINT badge */}
            <div style={{ position: 'absolute', top: 8, left: 8, display: 'flex', alignItems: 'center', gap: 5,
              background: 'rgba(0,0,0,0.65)', borderRadius: 3, padding: '2px 7px' }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%',
                background: isShodanCamera ? '#f90' : '#0f0', display: 'inline-block',
                boxShadow: isShodanCamera ? '0 0 5px #f90' : '0 0 5px #0f0' }} />
              <span style={{ fontSize: 9, fontFamily: 'monospace',
                color: isShodanCamera ? '#f90' : '#0f0', letterSpacing: '0.1em' }}>
                {isShodanCamera ? 'OSINT' : 'LIVE'}
              </span>
            </div>
            {/* Refresh button — only for live DOT cameras */}
            {isRefreshable && (
              <button
                title="Refresh thumbnail"
                onClick={() => { setThumbErr(false); setThumbTs(Date.now()) }}
                style={{ position: 'absolute', top: 8, right: 8, background: 'rgba(0,0,0,0.55)',
                  border: '1px solid rgba(255,102,0,0.4)', borderRadius: 3, color: 'var(--orange)',
                  fontSize: 13, cursor: 'pointer', padding: '2px 6px', lineHeight: 1 }}
              >↺</button>
            )}
            {/* Timestamp watermark */}
            <div style={{ position: 'absolute', bottom: 6, left: 8, fontSize: 9, fontFamily: 'monospace',
              color: 'rgba(255,255,255,0.35)' }}>
              {isShodanCamera ? 'Shodan capture' : new Date(thumbTs).toLocaleTimeString()}
            </div>
            {/* Source badge */}
            {publicCameraUrl && (
              <a href={publicCameraUrl} target="_blank" rel="noreferrer"
                style={{ position: 'absolute', bottom: 6, right: 8, fontSize: 9, fontFamily: 'monospace',
                  color: 'rgba(255,102,0,0.6)', textDecoration: 'none' }}>
                Open source &#x2197;
              </a>
            )}
          </div>
        ) : thumbErr && proxyThumb ? (
          /* ── Thumbnail failed to load ── */
          <div style={{ textAlign: 'center', padding: 24 }}>
            <div style={{ fontSize: 36, marginBottom: 10, opacity: 0.4 }}>📷</div>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--grey)', marginBottom: 6 }}>
              Thumbnail unavailable
            </div>
            <div style={{ fontSize: 10, color: '#555', marginBottom: 14, lineHeight: 1.5 }}>
              The camera image could not be loaded from the upstream DOT source.
            </div>
            <button
              onClick={() => { setThumbErr(false); setThumbTs(Date.now()) }}
              style={{ fontSize: 10, color: 'var(--orange)', background: 'none', border: '1px solid rgba(255,102,0,0.3)',
                borderRadius: 3, padding: '3px 10px', cursor: 'pointer', fontFamily: 'monospace' }}
            >↺ Retry</button>
            {publicCameraUrl && (
              <div style={{ marginTop: 12 }}>
                <a href={publicCameraUrl} target="_blank" rel="noreferrer"
                  style={{ fontSize: 10, color: 'var(--orange)', fontFamily: 'monospace' }}>
                  Open camera page &#x2197;
                </a>
              </div>
            )}
          </div>
        ) : (
          /* ── No feed available ── */
          <div style={{ color: 'var(--muted)', textAlign: 'center', padding: 24 }}>
            <div style={{ fontSize: 44, marginBottom: 12, opacity: 0.4 }}>📷</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--grey)', marginBottom: 8 }}>
              No feed configured
            </div>
            <div style={{ fontSize: 11, color: '#444', maxWidth: 320, lineHeight: 1.6 }}>
              This camera has no thumbnail URL or live stream. Add a thumbnail URL to the camera entry in the server seed data.
            </div>
            {publicCameraUrl && (
              <div style={{ marginTop: 14 }}>
                <a href={publicCameraUrl} target="_blank" rel="noreferrer"
                  style={{ fontSize: 11, color: 'var(--orange)', fontFamily: 'monospace' }}>
                  Open camera page &#x2197;
                </a>
              </div>
            )}
          </div>
        )}
        {/* Tactical overlay corners on video area */}
        <div style={{ position:'absolute',top:4,left:4, width:10,height:10, borderTop:'1px solid rgba(255,102,0,0.4)', borderLeft:'1px solid rgba(255,102,0,0.4)', pointerEvents:'none' }} />
        <div style={{ position:'absolute',bottom:4,right:4, width:10,height:10, borderBottom:'1px solid rgba(255,102,0,0.4)', borderRight:'1px solid rgba(255,102,0,0.4)', pointerEvents:'none' }} />
      </div>

      {/* Footer */}
      <div style={{
        padding: '5px 12px', display: 'flex', justifyContent: 'space-between',
        alignItems: 'center', borderTop: '1px solid rgba(255,102,0,0.15)', flexShrink: 0,
        fontSize: 9, color: 'var(--muted)', fontFamily: 'monospace',
        background: 'rgba(255,102,0,0.04)',
      }}>
        <span>&#x1F4CD; {camera.lat.toFixed(4)}&deg;, {camera.lon.toFixed(4)}&deg;</span>
        <span style={{ color: 'rgba(255,102,0,0.4)' }}>GODSEYE &middot; CAMERA FEED</span>
      </div>

      {/* Resize handle */}
      <div
        style={{
          position: 'absolute', bottom: 0, right: 0,
          width: 20, height: 20, cursor: 'se-resize',
          display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end',
          color: 'rgba(255,102,0,0.4)', fontSize: 12, padding: 2,
        }}
        onMouseDown={e => {
          e.stopPropagation()
          setResizing(true)
          resize.current = { mx: e.clientX, my: e.clientY, w: size.w, h: size.h }
        }}
      >&#9698;</div>
    </div>
  )
}
